/**
 * Vocabulary Set Drill Screen (route `VocabSetDrill`)
 *
 * The screen the whole feature exists for, and the one place a design decision
 * is doing real work rather than styling:
 *
 * **VSD-001 — the learner PRODUCES the word.** We show the Spanish and ask them
 * to type the English. Revealing is available, but taking it is an explicit
 * "show me" that records `encountered` and moves on.
 *
 * That is not a UX preference. ADR-034 defines mastery as production, and a
 * flashcard the learner taps is exposure. Calling a tap `produced` would
 * redefine "mastered" for every surface in the app at once (VSET-003). A
 * reveal-card drill would look like progress and never advance a single word.
 *
 * The other half is VSET-004, enforced server-side: a drill raises
 * `productions` and refreshes the SRS calendar but never `sessionsCount`, so a
 * learner can get most of the way to mastery here and has to finish it in
 * conversation.
 *
 * @see docs/specs/mobile/vocab-sets.mobile.md
 */

import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useRoute, type RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { ErrorState, LoadingState } from '../components/ui';
import { FS_XS, FS_SM, FS_MD } from '../theme';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useResolveBilingual } from '../hooks/useResolveBilingual';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { errorMessage } from '../generated/api/endpoint-errors';
import {
  selectLoadVocabSet,
  selectMarkVocabSetItem,
  selectVocabSetDetailLoading,
  selectVocabSetMarkErrorInfo,
  selectVocabSetsErrorInfo,
  useVocabSetsStore,
} from '../store/vocabSetsStore';
import { isCorrectAnswer, evaluateSpokenAttempt } from '../components/vocabsets/answerMatch';
import { VoiceRecorder } from '../components/chat/VoiceRecorder';
import type { PronunciationResult } from '../types/conversation';
import type { VocabSetMemberItem } from '../generated/api/http-contracts';

type DrillParams = { VocabSetDrill: { slug: string } };
type DrillRoute = RouteProp<DrillParams, 'VocabSetDrill'>;

export function VocabSetDrillScreen() {
  const { t } = useTranslation();
  const { theme } = useDisplayTheme();
  const route = useRoute<DrillRoute>();
  const slug: string | undefined = (route.params as DrillParams['VocabSetDrill'] | undefined)
    ?.slug;
  const resolve = useResolveBilingual();
  const courseId = useUserPreferencesStore((s) => s.activeCourseId);

  const detail = useVocabSetsStore((s) => (slug ? s.detail[slug] : undefined));
  const detailLoading = useVocabSetsStore(selectVocabSetDetailLoading);
  const errorInfo = useVocabSetsStore(selectVocabSetsErrorInfo);
  const markError = useVocabSetsStore(selectVocabSetMarkErrorInfo);
  const loadSet = useVocabSetsStore(selectLoadVocabSet);
  const mark = useVocabSetsStore(selectMarkVocabSetItem);

  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  /** Last spoken attempt's scores — shown as FEEDBACK only (VSD-002). */
  const [spoken, setSpoken] = useState<{ score: number; reason: string } | null>(null);

  useFocusEffect(
    useCallback(() => {
      if (courseId && slug) void loadSet(courseId, slug);
    }, [loadSet, courseId, slug]),
  );

  /**
   * Mastered words stay in the set and stay drillable (VSM-004) — dimmed and
   * last, not removed. A set that empties as you learn it stops being "the
   * colours" and becomes a to-do list, and the SRS ladder wants them back.
   */
  const queue = useMemo(() => {
    const m = detail?.members ?? [];
    return [...m].sort(
      (a, b) => Number(a.status === 'mastered') - Number(b.status === 'mastered'),
    );
  }, [detail]);

  const current: VocabSetMemberItem | undefined = queue[index];

  const advance = useCallback(() => {
    setTyped('');
    setRevealed(false);
    setSpoken(null);
    setIndex((i) => (i + 1) % Math.max(queue.length, 1));
  }, [queue.length]);

  const submit = useCallback(async () => {
    if (!current || !courseId || !slug || busy) return;
    setBusy(true);
    const right = isCorrectAnswer(typed, current.lemma, current.translation.en_GB);
    const status = await mark(courseId, slug, current.senseId, right ? 'produced' : 'struggled');
    setBusy(false);
    // A failed mark leaves the item exactly where it was (VSM-008): advancing
    // would show the learner progress the server never recorded.
    if (status === null) return;
    if (right) advance();
    else setRevealed(true);
  }, [current, courseId, slug, typed, mark, advance, busy]);

  /**
   * VSD-002 — a spoken attempt.
   *
   * Recall decides the mark; the pronunciation scores are shown and change
   * nothing (see `evaluateSpokenAttempt`). Saying the right word badly is still
   * knowing the word, and the Sound Map is the surface whose job is the accent.
   */
  const handleSpoken = useCallback(
    async (transcription: string, pronunciation: PronunciationResult | undefined) => {
      setRecording(false);
      if (!current || !courseId || !slug) return;

      const verdict = evaluateSpokenAttempt(
        transcription,
        current.lemma,
        current.translation.en_GB,
        pronunciation?.completenessScore,
      );
      setSpoken({
        score: Math.round(pronunciation?.pronunciationScore ?? 0),
        reason: verdict.reason,
      });

      setBusy(true);
      const status = await mark(
        courseId,
        slug,
        current.senseId,
        verdict.correct ? 'produced' : 'struggled',
      );
      setBusy(false);
      if (status === null) return;
      if (verdict.correct) advance();
      else setRevealed(true);
    },
    [current, courseId, slug, mark, advance],
  );

  const reveal = useCallback(async () => {
    if (!current || !courseId || !slug || busy) return;
    setBusy(true);
    // Revealing is EXPOSURE, never production (VSET-003).
    const status = await mark(courseId, slug, current.senseId, 'encountered');
    setBusy(false);
    if (status === null) return;
    setRevealed(true);
  }, [current, courseId, slug, mark, busy]);

  if (detailLoading && !detail) {
    return (
      <>
        <ScreenHeader title={t('vocabSets.drillTitle')} />
        <LoadingState testID="vocab-set-drill-loading" message={t('vocabSets.loading')} />
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <ScreenHeader title={t('vocabSets.drillTitle')} />
        <ErrorState
          testID="vocab-set-drill-error"
          message={errorInfo ? errorMessage(errorInfo) : t('vocabSets.loadError')}
          retryLabel={t('common.retry')}
          onRetry={() => {
            if (courseId && slug) void loadSet(courseId, slug);
          }}
        />
      </>
    );
  }

  const title = resolve(detail.set.title);
  const p = detail.set.progress;

  return (
    <>
      <ScreenHeader title={title} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <ScrollView contentContainerStyle={styles.content} testID="vocab-set-drill-scroll">
          <Text style={[styles.count, { color: theme.textSecondary }]} testID="vocab-set-progress">
            {t('vocabSets.countLabel', { done: p.mastered, total: p.total })}
          </Text>

          {current ? (
            <View
              style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
              testID="vocab-set-drill-card"
            >
              {/* The PROMPT is Spanish — the learner produces the English. */}
              <Text style={[styles.prompt, { color: theme.text }]} testID="vocab-set-prompt">
                {resolve(current.translation)}
              </Text>
              <Text style={[styles.pos, { color: theme.textSecondary }]}>{current.pos}</Text>

              {revealed ? (
                <>
                  <Text style={[styles.answer, { color: theme.text }]} testID="vocab-set-answer">
                    {current.lemma}
                  </Text>
                  <TouchableOpacity
                    onPress={advance}
                    testID="vocab-set-next"
                    accessibilityRole="button"
                    accessibilityLabel={t('vocabSets.next')}
                    style={[styles.btn, { backgroundColor: theme.primary }]}
                  >
                    <Text style={styles.btnText}>{t('vocabSets.next')}</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <TextInput
                    value={typed}
                    onChangeText={setTyped}
                    onSubmitEditing={submit}
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder={t('vocabSets.inputPlaceholder')}
                    placeholderTextColor={theme.textSecondary}
                    testID="vocab-set-input"
                    accessibilityLabel={t('vocabSets.inputPlaceholder')}
                    style={[styles.input, { borderColor: theme.border, color: theme.text }]}
                  />
                  <TouchableOpacity
                    onPress={submit}
                    disabled={busy}
                    testID="vocab-set-submit"
                    accessibilityRole="button"
                    accessibilityLabel={t('vocabSets.check')}
                    style={[styles.btn, { backgroundColor: theme.primary, opacity: busy ? 0.6 : 1 }]}
                  >
                    <Text style={styles.btnText}>{t('vocabSets.check')}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => setRecording(true)}
                    disabled={busy}
                    testID="vocab-set-speak"
                    accessibilityRole="button"
                    accessibilityLabel={t('vocabSets.speak')}
                    style={[styles.btnOutline, { borderColor: theme.primary }]}
                  >
                    <Text style={[styles.btnOutlineText, { color: theme.primary }]}>
                      {t('vocabSets.speak')}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={reveal}
                    disabled={busy}
                    testID="vocab-set-reveal"
                    accessibilityRole="button"
                    accessibilityLabel={t('vocabSets.reveal')}
                  >
                    <Text style={[styles.link, { color: theme.textSecondary }]}>
                      {t('vocabSets.reveal')}
                    </Text>
                  </TouchableOpacity>
                </>
              )}
            </View>
          ) : (
            <Text style={{ color: theme.textSecondary }} testID="vocab-set-empty">
              {t('vocabSets.empty')}
            </Text>
          )}

          {spoken ? (
            <Text
              style={[styles.spoken, { color: theme.textSecondary }]}
              testID="vocab-set-spoken-feedback"
            >
              {spoken.reason === 'not-spoken'
                ? t('vocabSets.notHeard')
                : t('vocabSets.pronunciationScore', { score: spoken.score })}
            </Text>
          ) : null}

          {markError ? (
            <Text style={[styles.markError, { color: theme.error }]} testID="vocab-set-mark-error">
              {errorMessage(markError) || t('vocabSets.markError')}
            </Text>
          ) : null}
        </ScrollView>
      </WebContainer>

      {/* Scripted assessment against the single target word. */}
      <VoiceRecorder
        isRecording={recording}
        onStop={handleSpoken}
        onCancel={() => setRecording(false)}
        referenceText={current?.lemma}
      />
    </>
  );
}

const styles = StyleSheet.create({
  content: { padding: 16, paddingBottom: 40 },
  count: { fontSize: FS_SM, marginBottom: 12 },
  card: { borderRadius: 12, borderWidth: 1, padding: 20 },
  prompt: { fontSize: 22, fontWeight: '600', textAlign: 'center' },
  pos: { fontSize: FS_XS, marginTop: 4, textAlign: 'center' },
  answer: { fontSize: 20, fontWeight: '700', marginTop: 18, textAlign: 'center' },
  input: {
    borderRadius: 8,
    borderWidth: 1,
    fontSize: FS_MD,
    marginTop: 18,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  btn: { alignItems: 'center', borderRadius: 8, marginTop: 14, paddingVertical: 12 },
  btnText: { color: '#fff', fontSize: FS_MD, fontWeight: '600' },
  link: { fontSize: FS_SM, marginTop: 14, textAlign: 'center' },
  btnOutline: {
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 10,
    paddingVertical: 12,
  },
  btnOutlineText: { fontSize: FS_MD, fontWeight: '600' },
  spoken: { fontSize: FS_SM, marginTop: 12, textAlign: 'center' },
  markError: { fontSize: FS_SM, marginTop: 16, textAlign: 'center' },
});
